<!--
A radio field with an input, on which all vee validation rules can be applied throught the "rules" prop.
See vee-validationrules.ts for list of available rules

Example use : 
 <RadioWithInputAndValidation
    rules="required"
    :tooltip="$t('input-email-tooltip')"
    :label="$t('input-email')"
    inputPlaceholder="e.g. EXAMPLE VALUE"
    v-model="something"
    :data="listofthings"
    :optionLabels="labelsForListOfThings"
    :inputLabel="e.g AUTRE : "
  />
-->
<template>
  <ValidationProvider
    v-slot="{ errors, valid }"
    :vid="(group != '' ? group + '_' : '') + vid"
    :name="($attrs.name as string) || ($attrs.label as string)"
    :rules="rulesWithoutRequiredRulesIfValidationDisabled"
    :model-value="innerValue"
    @update:model-value="updateValue"
  >
    <b-field
      :type="{
        'is-danger': showValidationErrors && errors[0],
        'is-success': showValidationErrors && valid,
      }"
      :message="showValidationErrors ? [errors[0]] : ''"
      class="RadioWithInputAndValidation-field"
      :class="[{ 'vertical-radios': verticalDisplay }, $attrs.class || '']"
    >
      <template #label>
        <FormInputLabel
          :validation-activated="validationActivated"
          :show-validation-errors="showValidationErrors"
          :label="($attrs.label as string) || ''"
          :is-mandatory="isMandatory"
          :disabled="disabled"
          :input-notice="inputNotice"
          :tooltip="tooltip"
        />
      </template>

      <div>
        <div v-for="(option, i) in data" :key="i" class="field">
          <b-radio
            v-model="innerValue"
            :native-value="option !== undefined ? (option as any) : undefined"
            :disabled="disabled"
          >
            <div
              :class="
                helpNotices && helpNotices.length !== 0
                  ? 'RadioWithInputAndValidation-main-field'
                  : ''
              "
            >
              {{ optionLabels[i] }}
            </div>
            <div
              v-if="helpNotices && helpNotices.length !== 0"
              class="help"
              v-html="helpNotices[i]"
            ></div>
          </b-radio>
        </div>

        <div v-if="Boolean(selectLabel)" class="field">
          <b-radio
            v-model="innerValue"
            :native-value="selectLabel"
            :class="
              helpNotices && helpNotices.length !== 0
                ? 'RadioWithInputAndValidation-main-field'
                : ''
            "
            :disabled="disabled"
          >
            {{ selectLabel }}
          </b-radio>

          <b-field :type="selectType">
            <b-select
              v-model="selectValue"
              class="RadioWithInputAndValidation-select"
              :placeholder="selectPlaceholder"
              :disabled="disabled || innerValue !== selectLabel"
              :expanded="true"
              @update:model-value="onSelectChange"
            >
              <template v-for="option in selectData" :key="option">
                <option :value="option">
                  {{ option }}
                </option>
              </template>
            </b-select>
          </b-field>
        </div>

        <div v-if="Boolean(inputLabel)" class="field RadioWithInputAndValidation-inputField">
          <b-radio
            v-model="innerValue"
            :native-value="inputLabel"
            :class="
              helpNotices && helpNotices.length !== 0
                ? 'RadioWithInputAndValidation-main-field'
                : ''
            "
            :disabled="disabled"
          >
            {{ inputLabel }}
          </b-radio>
          <ValidationProvider
            v-slot="{ errors: inputErrors }"
            :vid="(group != '' ? group + '_' : '') + vid + '_radioInput'"
            :name="inputLabel"
            :rules="`${validationActivated && inputType === 'is-danger' ? 'required' : ''}`"
            class="RadioWithInputAndValidation-inputProvider"
          >
            <b-field :type="inputType">
              <b-input
                v-model="inputValue"
                class="RadioWithInputAndValidation-input"
                :placeholder="inputPlaceholder"
                :disabled="disabled || innerValue !== inputLabel"
                @update:model-value="onInputChange"
              ></b-input>
            </b-field>
            <p v-if="showValidationErrors && inputErrors.length > 0" class="help is-danger">
              {{ inputErrors[0] }}
            </p>
          </ValidationProvider>
        </div>
      </div>
    </b-field>
  </ValidationProvider>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import ValidationProvider from "@/services/validation/ValidationProvider.vue"
import FormInputLabel from "./FormInputLabel.vue"
import { BField, BInput, BRadio, BSelect } from "buefy"
import { filterRulesByValidationStatus } from "@/services/validation/validation-rules-utils"

export default defineComponent({
  name: "RadioWithInputAndValidation",
  components: {
    ValidationProvider,
    FormInputLabel,
    BInput,
    BField,
    BSelect,
    BRadio,
  },
  props: {
    group: {
      type: String,
      default: "",
    },
    vid: {
      type: String,
      required: false,
      default: "",
    },
    rules: {
      type: [String],
      required: false,
      default: "",
    },
    modelValue: {
      type: [String, Boolean],
      required: false,
      default: "",
    },
    tooltip: {
      type: String,
      required: false,
      default: "",
    },
    data: {
      type: Array,
      required: false,
      default: () => [],
    },
    optionLabels: {
      type: Array,
      required: false,
      default: () => [],
    },
    inputLabel: {
      type: String,
      required: false,
      default: "",
    },
    inputNotice: {
      type: String,
      required: false,
      default: "",
    },
    inputPlaceholder: {
      type: String,
      default: "",
    },
    verticalDisplay: {
      type: Boolean,
      required: false,
    },
    helpNotices: {
      type: Array,
      required: false,
      default: () => [],
    },
    selectLabel: {
      type: String,
      required: false,
      default: "",
    },
    selectData: {
      type: Array,
      required: false,
      default: () => [],
    },
    selectPlaceholder: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: "",
    },
    validationActivated: {
      type: Boolean,
      default: true,
    },
    showValidationErrors: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["update:modelValue", "manual-change", "input-change", "select-change"],
  data() {
    return {
      inputValue: "",
      innerValue: undefined as string | boolean | undefined,
      inputType: "",
      selectValue: "",
      selectType: "",
      isMandatory: false,
      rulesWithoutRequiredRulesIfValidationDisabled: "",
    }
  },
  watch: {
    innerValue(
      newVal: string | boolean | undefined,
      _oldValue: string | boolean | undefined,
    ): void {
      this.inputType = ""
      this.selectType = ""
      if (Boolean(this.inputLabel) && this.innerValue === this.inputLabel) {
        this.inputType = this.inputValue ? "is-success" : "is-danger"
      }
      if (Boolean(this.selectLabel) && this.innerValue === this.selectLabel) {
        this.selectType = this.selectValue ? "is-success" : "is-danger"
      }
      this.$emit("update:modelValue", newVal)
    },
    modelValue(
      newVal: string | boolean | undefined,
      _oldValue: string | boolean | undefined,
    ): void {
      this.innerValue = newVal
      if (_oldValue !== undefined && newVal !== _oldValue) {
        this.$emit("manual-change", newVal)
      }
    },
    rules(): void {
      this.onRulesChanged()
    },
    validationActivated(): void {
      this.onRulesChanged()
    },
  },
  created(): void {
    if (this.modelValue !== null && this.modelValue !== undefined) {
      this.innerValue = this.modelValue
    }
    // Handle both string and array formats for rules in created
    if (this.rules) {
      if (Array.isArray(this.rules)) {
        this.isMandatory =
          this.rules.includes("required") || this.rules.includes("required_boolean")
      } else {
        this.isMandatory =
          this.rules.includes("required") || this.rules.includes("required_boolean")
      }
    } else {
      this.isMandatory = false
    }
    this.onRulesChanged()
  },
  methods: {
    updateValue(newValue: string | boolean | undefined): void {
      this.innerValue = newValue
    },

    onInputChange(): void {
      this.inputType = this.inputValue ? "is-success" : "is-danger"
      this.$emit("input-change", this.inputValue)
    },

    onSelectChange(): void {
      this.selectType = this.selectValue ? "is-success" : "is-danger"
      this.$emit("select-change", this.selectValue)
    },

    onRulesChanged(): void {
      // Handle both string and array formats for rules
      if (this.rules) {
        if (Array.isArray(this.rules)) {
          this.isMandatory =
            this.rules.includes("required") || this.rules.includes("required_boolean")
        } else {
          // String format like "required|email"
          this.isMandatory =
            this.rules.includes("required") || this.rules.includes("required_boolean")
        }
      } else {
        this.isMandatory = false
      }

      if (this.rules) {
        this.rulesWithoutRequiredRulesIfValidationDisabled = this.rules.toString()
        if (!this.validationActivated) {
          this.rulesWithoutRequiredRulesIfValidationDisabled = filterRulesByValidationStatus(
            this.rules,
            this.validationActivated,
          )
        }
      } else {
        this.rulesWithoutRequiredRulesIfValidationDisabled = ""
      }
    },
  },
})
</script>

<style lang="scss" scoped>
@import "@/styles/variables";

.RadioWithInputAndValidation-field {
  margin-bottom: $input-field-margin-bot;
}

.fieldLabel {
  display: flex;
  justify-content: space-between;
  width: 100%;

  .mandatory {
    padding-left: 20px;
    color: $dark;
  }
}

.field {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  margin-bottom: 12px;
  padding-top: 12px;
  flex-direction: column;
}

.RadioWithInputAndValidation-inputProvider {
  width: 100%;
}

.vertical-radios .field.has-addons {
  flex-direction: column;
}

.RadioWithInputAndValidation-main-field {
  font-weight: 600;
}

.field .b-radio.radio {
  align-items: flex-start;
}

#app [disabled].radio {
  color: $text-default-color;
}
</style>
